import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PersonIcon from "@mui/icons-material/Person";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../../firebase";
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from "date-fns";

function Attendnacereport() {
  // Date Filters (Defaults to current month start -> today)
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(format(firstDayOfMonth, "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(today, "yyyy-MM-dd"));
  const [selectedStaffId, setSelectedStaffId] = useState("ALL");

  const [staffList, setStaffList] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Staff List & sort A-Z
      const staffSnap = await getDocs(query(collection(db, "authroles")));
      const staffData = staffSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      staffData.sort((a, b) => {
        const nameA = (a.name || a.staffName || a.email || "").toLowerCase();
        const nameB = (b.name || b.staffName || b.email || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setStaffList(staffData);

      // 2. Fetch Attendance Records
      const attSnap = await getDocs(collection(db, "staffattendance"));
      const records = attSnap.docs.map((doc) => ({
        docId: doc.id,
        ...doc.data(),
      }));
      setAttendanceRecords(records);
    } catch (error) {
      console.error("Error fetching report data:", error);
    }
    setLoading(false);
  };

  // Helper to filter attendance records by date range
  const getFilteredRecords = () => {
    if (!startDate || !endDate) return attendanceRecords;

    const start = startOfDay(parseISO(startDate));
    const end = endOfDay(parseISO(endDate));

    return attendanceRecords.filter((rec) => {
      if (!rec.date) return false;
      const recDate = parseISO(rec.date);
      return isWithinInterval(recDate, { start, end });
    });
  };

  const filteredRecords = getFilteredRecords();

  // Compute aggregated stats for ALL staff
  const getStaffSummary = () => {
    return staffList.map((staff) => {
      const staffLogs = filteredRecords.filter(
        (r) => r.staffId === staff.id || r.uid === staff.id
      );

      const present = staffLogs.filter((r) => r.status === "Present").length;
      const absent = staffLogs.filter((r) => r.status === "Absent").length;
      const late = staffLogs.filter((r) => r.status === "Late Mark").length;
      const halfDay = staffLogs.filter((r) => r.status === "Half Day").length;

      return {
        id: staff.id,
        name: staff.name || staff.staffName || staff.email || "Unknown Staff",
        role: staff.role || "Staff",
        totalLogs: staffLogs.length,
        present,
        absent,
        late,
        halfDay,
      };
    });
  };

  // Compute logs for single selected staff
  const getSingleStaffLogs = () => {
    if (selectedStaffId === "ALL") return [];
    return filteredRecords
      .filter((r) => r.staffId === selectedStaffId || r.uid === selectedStaffId)
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  const selectedStaffObj = staffList.find((s) => s.id === selectedStaffId);
  const staffSummaryList = getStaffSummary();
  const singleStaffLogs = getSingleStaffLogs();

  // Print Action
  const handlePrint = () => {
    window.print();
  };

  return (
    <Box p={1}>
      {/* Header Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={1}
        className="no-print"
      >
        <Box>
          <Typography variant="h6" fontWeight="bold" color="primary">
            Staff Attendance Report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Generate, filter, and print detailed staff attendance records
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ bgcolor: "#1a237e" }}
        >
          Print Report
        </Button>
      </Stack>

      {/* Filter Options */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 1, borderRadius: 3 }} className="no-print">
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <FilterAltIcon color="action" />
          <Typography variant="subtitle1" fontWeight="bold">
            Report Filters
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Staff</InputLabel>
              <Select
                value={selectedStaffId}
                label="Select Staff"
                onChange={(e) => setSelectedStaffId(e.target.value)}
              >
                <MenuItem value="ALL">
                  <em>All Staff Members (Summary)</em>
                </MenuItem>
                {staffList.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {staff.name || staff.staffName || staff.email || staff.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setStartDate(format(firstDayOfMonth, "yyyy-MM-dd"));
                setEndDate(format(today, "yyyy-MM-dd"));
                setSelectedStaffId("ALL");
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading Indicator */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        /* Printable Target Area */
        <Box className="printable-area">
          {/* Print Header Information */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight="bold" align="center">
              Staff Attendance Report
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary">
              Period: {startDate ? format(parseISO(startDate), "dd MMM yyyy") : ""} to{" "}
              {endDate ? format(parseISO(endDate), "dd MMM yyyy") : ""}
            </Typography>
            {selectedStaffId !== "ALL" && selectedStaffObj && (
              <Typography variant="subtitle2" align="center" fontWeight="bold" mt={0.5}>
                Staff Member: {selectedStaffObj.name || selectedStaffObj.staffName || selectedStaffObj.email}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
          </Box>

          {/* Summary Cards */}
          <Grid className="row" spacing={2} mb={3}>
            <Grid className="col-sm-3 mb-1" item xs={3}>
              <Card sx={{ bgcolor: "#e8f5e9", border: "1px solid #a5d6a7" }}>
                <CardContent sx={{ py: 0.1, "&:last-child": { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    TOTAL PRESENT
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                    {filteredRecords.filter((r) => r.status === "Present").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid className="col-sm-3 mb-1" item xs={3}>
              <Card sx={{ bgcolor: "#fff3e0", border: "1px solid #ffcc80" }}>
                <CardContent sx={{ py: 0.1, "&:last-child": { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    LATE MARKS
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#ed6c02">
                    {filteredRecords.filter((r) => r.status === "Late Mark").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid className="col-sm-3 mb-1" item xs={3}>
              <Card sx={{ bgcolor: "#e3f2fd", border: "1px solid #bbdefb" }}>
                <CardContent sx={{ py: 0.1, "&:last-child": { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    HALF DAYS
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#1976d2">
                    {filteredRecords.filter((r) => r.status === "Half Day").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid className="col-sm-3 mb-1" item xs={3}>
              <Card sx={{ bgcolor: "#ffebee", border: "1px solid #ef9a9a" }}>
                <CardContent sx={{ py: 0.1, "&:last-child": { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    ABSENT MARKS
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#d32f2f">
                    {filteredRecords.filter((r) => r.status === "Absent").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* VIEW 1: ALL STAFF SUMMARY TABLE */}
          {selectedStaffId === "ALL" && (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box p={2} bgcolor="#f8f9fa" className="no-print">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SummarizeIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    All Staff Attendance Overview
                  </Typography>
                </Stack>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#1a237e" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Staff Name</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Role</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Present</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Late</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Half Day</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Absent</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total Logged</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffSummaryList.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: "bold" }}>{row.name}</TableCell>
                        <TableCell>{row.role}</TableCell>
                        <TableCell align="center" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                          {row.present}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#ed6c02", fontWeight: "bold" }}>
                          {row.late}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                          {row.halfDay}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#d32f2f", fontWeight: "bold" }}>
                          {row.absent}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {row.totalLogs}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* VIEW 2: INDIVIDUAL STAFF DETAILED REPORT */}
          {selectedStaffId !== "ALL" && (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box p={2} bgcolor="#f8f9fa" className="no-print">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Attendance Log: {selectedStaffObj?.name || selectedStaffObj?.staffName || selectedStaffObj?.email}
                  </Typography>
                </Stack>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#1a237e" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Date</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Check In</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>In Location</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Check Out</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Out Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleStaffLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No attendance entries found for the selected date range.
                        </TableCell>
                      </TableRow>
                    ) : (
                      singleStaffLogs.map((log) => (
                        <TableRow key={log.docId} hover>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {log.date ? format(parseISO(log.date), "dd MMM yyyy (EEE)") : "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.status}
                              color={
                                log.status === "Present"
                                  ? "success"
                                  : log.status === "Late Mark"
                                  ? "warning"
                                  : log.status === "Half Day"
                                  ? "info"
                                  : log.status === "Absent"
                                  ? "error"
                                  : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {log.checkInTime ? format(parseISO(log.checkInTime), "hh:mm a") : "--:--"}
                          </TableCell>
                          <TableCell>
                            {log.checkInLocation || log.location || "-"}
                          </TableCell>
                          <TableCell>
                            {log.checkOutTime ? format(parseISO(log.checkOutTime), "hh:mm a") : "--:--"}
                          </TableCell>
                          <TableCell>
                            {log.checkOutLocation || "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Embedded CSS Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm; size: landscape; }
        }
      `}</style>
    </Box>
  );
}

export default Attendnacereport;